import React from 'react';
import { Modal, Form, Input, Select, Upload, App, Progress } from 'antd';
import { InboxOutlined, FileImageOutlined } from '@ant-design/icons';
import type { RcFile, UploadProps } from 'antd/es/upload/interface';
import { logger } from '../../services/logger.client';
import type { ImageCategory } from '../../types/types';

const { Dragger } = Upload;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface AddImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the user-entered name, selected category, and the client-side
   * compressed File. The container performs the actual upload API call.
   */
  onUpload: (name: string, category: string, compressedFile: File) => Promise<void>;
  /** Available image categories from the global cache. */
  imageCategories: ImageCategory[];
  /** Whether an upload is currently in progress (disables form interaction). */
  isUploading: boolean;
  /** Upload progress percentage (0-100). */
  uploadProgress: number;
}

/**
 * Compresses an image from a data URL.
 * This is a local utility (dataURL -> canvas -> dataURL) -- no external API call.
 *
 * @param dataUrl - The Base64 data URL of the image.
 * @param options - Compression options.
 * @returns A promise that resolves with the compressed data URL.
 */
const compressImage = (dataUrl: string, options = { quality: 0.7, maxWidth: 800, maxHeight: 800 }): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;

            if (width > height) {
                if (width > options.maxWidth) {
                    height = Math.round(height * (options.maxWidth / width));
                    width = options.maxWidth;
                }
            } else {
                if (height > options.maxHeight) {
                    width = Math.round(width * (options.maxHeight / height));
                    height = options.maxHeight;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return reject(new Error('Could not get canvas context'));
            }
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', options.quality));
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
};

/** Reads a file as a base64 data URL. */
const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

/**
 * Pure presenter for the Add Image modal (ADR-034).
 *
 * Handles local UI concerns:
 * - Form validation and field management
 * - Client-side image compression (canvas-based, no API call)
 * - File size warnings
 *
 * Delegates the actual upload to the container via `onUpload`.
 * Zero store access. Zero API calls.
 */
const AddImageModal: React.FC<AddImageModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  imageCategories,
  isUploading,
  uploadProgress,
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (values.file.length === 0 || !values.file[0].originFileObj) {
        message.error('Please select an image file.');
        return;
      }

      const file: RcFile = values.file[0].originFileObj;

      // Compress the image before uploading.
      // The `fetch(compressedDataUrl)` below converts a local data URL to a Blob --
      // this is an in-memory browser operation, NOT an external API call.
      const base64 = await getBase64(file);
      const compressedDataUrl = await compressImage(base64);
      const compressedBlob = await (await fetch(compressedDataUrl)).blob();
      const compressedFile = new File([compressedBlob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      await onUpload(values.name, values.category, compressedFile);

      message.success(`Image "${values.name}" has been added.`);
      form.resetFields();
      onClose();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred during upload.';
      const errorContext = error && typeof error === 'object' ? error : { message: errorMessage };
      logger.error({ error: errorContext }, 'Add Image validation failed.');
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      const isTooLarge = file.size > MAX_FILE_SIZE_BYTES;
      if (isTooLarge) {
        message.error(`File is too large. The maximum size is ${MAX_FILE_SIZE_MB}MB. The selected file will be compressed on upload, but please choose a smaller file if possible.`);
        // Even if it's too large, we allow it to be selected because our compression might fix it.
        // The server will have the final say.
      }

      // By returning false, we stop the default upload behavior
      // and let the form handle the file.
      return false; // Prevent auto-upload
    },
    listType: "picture",
    maxCount: 1,
  };

  return (
    <Modal
      title={<><FileImageOutlined style={{ marginRight: 8 }} />Add New Image</>}
      open={isOpen}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={isUploading}
      destroyOnHidden
      footer={isUploading ? null : undefined} // Hide default footer during upload
    >
      {isUploading && <Progress percent={uploadProgress} />}
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} name="add_image_form" initialValues={{ category: 'Other' }}>
        <Form.Item
          name="name"
          label="Image Name"
          rules={[{ required: true, message: 'Please input the name for the image!' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: 'Please select a category!' }]}
        >
          <Select>
            {(imageCategories ?? []).map((cat: ImageCategory) => <Select.Option key={cat.id} value={cat.id}>{cat.name}</Select.Option>)}
          </Select>
        </Form.Item>
        <Form.Item
          name="file"
          label="Image File"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
          rules={[{ required: true, message: 'Please select an image file!' }]}
        >
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Single file upload. Max {MAX_FILE_SIZE_MB}MB. Images are automatically compressed.
            </p>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddImageModal;